import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from '../static/styles/EditProfile.module.css';
import { getUserProfile, updateUserProfile } from '../api/profile';
import dummyAvatar from '../static/img/Sidebar/Avatar.png';
import MessagePopup from '../components/MessagePopup';
import { trackProfileUpdate } from '../utils/analytics';

const EditProfile = () => {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [gender, setGender] = useState("");
  const [bio, setBio] = useState("");
  const [profileImage, setProfileImage] = useState(dummyAvatar);
  const [stats, setStats] = useState({
    tournaments: 0,
    completedTasks: 0,
    credits: 0,
    redeemedItems: 0
  });

  const [loading, setLoading] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Edit Profile | Jazz ESL";
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      const response = await getUserProfile();
      if (response.success) {
        setUsername(response.username || "");
        setEmail(response.email || "");
        setGender(response.gender?.toLowerCase() || "");
        setBio(response.bio || "");
        setDateOfBirth(
          response.dateOfBirth
            ? response.dateOfBirth.split("-").reverse().join("-")
            : ""
        );
        setProfileImage(response.profileImage || dummyAvatar);
        setStats({
          tournaments: response.total_tournament || 0,
          completedTasks: response.completed_tasks_total || 0,
          credits: response.credits || 0,
          redeemedItems: response.items_redeemed || 0
        });
      }
    } catch (error) {
    }
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        alert("Please select a valid image file.");
        return;
      }
      if (file.size > 2 * 1024 * 1024) { // 2MB limit
        alert("File size must not exceed 2MB.");
        return;
      }
      const reader = new FileReader();
      reader.onload = () => setProfileImage(reader.result); // Store Base64 string in profileImage
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    setLoading(true); // Start loading
    try {
      const profileData = {
        username,
        email,
        gender,
        bio,
        dateOfBirth,
        image: profileImage // Include Base64 string in the payload
      };

      const response = await updateUserProfile(profileData);

      if (response.status) {
        trackProfileUpdate(true);
        setPopupMessage("Profile updated successfully!");
        setIsPopupOpen(true);
        await fetchUserProfile();
      } else {
        trackProfileUpdate(false);
        setPopupMessage(`Failed to update profile: ${response.message}`);
        setIsPopupOpen(true);
      }
    } catch (error) {
      setPopupMessage("An error occurred while updating the profile.");
      setIsPopupOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.editProfile}>
      <div className={styles.header}>
        <span className={styles.back} onClick={() => navigate(-1)}>‹</span>
        <h3 className={styles.title}>Edit Profile</h3>
      </div>

      <div className={styles.statsBox}>
        <div className={styles.stat}>
          <div className={styles.value}>{stats.tournaments}</div>
          <div className={styles.label}>Tournaments played</div>
        </div>
        <div className={styles.stat}>
          <div className={styles.value}>{stats.completedTasks}</div>
          <div className={styles.label}>Completed Tasks</div>
        </div>
        <div className={styles.stat}>
          <div className={styles.value}>{stats.credits}</div>
          <div className={styles.label}>Credits Earned</div>
        </div>
        <div className={styles.stat}>
          <div className={styles.value}>{stats.redeemedItems}</div>
          <div className={styles.label}>Redeemed Items</div>
        </div>
      </div>

      <div className={styles.profileImageSection}>
        <div className={styles.avatarCircle}>
          <label htmlFor="imageUpload">
            <img src={profileImage} alt="Profile" className={styles.profileImage} />
          </label>
          <input
            id="imageUpload"
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleImageChange}
          />
        </div>
        <label htmlFor="imageUpload" className={styles.changeImageText}>
          Change image
        </label>
      </div>

      <div className={styles.formGroup}>
        <label>Username</label>
        <input value={username} onChange={e => setUsername(e.target.value)} placeholder="Enter username" />
      </div>
      <div className={styles.formGroup}>
        <label>Email</label>
        <input
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="Enter email address"
        />
      </div>
      <div className={styles.formGroup}>
        <label>Gender</label>
        <select value={gender} onChange={e => setGender(e.target.value)}>
          <option value="">Select gender</option>
          <option value="male">Male</option>
          <option value="female">Female</option>
        </select>
      </div>
      <div className={styles.formGroup}>
        <label>Bio</label>
        <input value={bio} onChange={e => setBio(e.target.value)} placeholder="Write something about yourself" />
      </div>
      <div className={styles.formGroup}>
        <label>Date of Birth</label>
        <input
          type="date"
          value={dateOfBirth}
          onChange={e => setDateOfBirth(e.target.value)}
          placeholder="Select your date of birth"
        />
      </div>

      <button
        className={`${styles.saveButton} ${loading ? styles.loading : ''}`}
        onClick={handleSave}
        disabled={loading}
      >
        {loading ? "Saving..." : "Save Changes"}
      </button>

      <MessagePopup
        isOpen={isPopupOpen}
        message={popupMessage}
        onClose={() => setIsPopupOpen(false)}
      />
    </div>
  );
};

export default EditProfile;